/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { Image } from '@fluentui/react/lib/Image';
import { ILabelProps, Label } from '@fluentui/react/lib/Label';
import { Stack } from '@fluentui/react/lib/Stack';
import { FontWeights } from '@fluentui/react/lib/Styling';
import { Property } from 'csstype';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { CalloutButton } from 'src/core/components/CalloutButton';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { LuisText } from 'src/core/components/LuisText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getStylistV2 } from 'src/core/themes/luisStylist';
import { getAssetsUrl } from 'src/core/utils/api/getAssetsUrl';
import { AutotagTriggerContentLeftMargin } from 'src/customText/verticals/autotagTriggerModal/autotagTriggerModal';
import { AutoLabelingModalFooter } from 'src/customText/verticals/autotagTriggerModal/components/AutoLabelingModalFooter';
import { AutoLabelingInfoDto, AutoLabelingSteps } from 'src/customText/verticals/autotagTriggerModal/types';

export type AutotagTriggerModalSelectTrainedModelProps = {
    autotagInfoDto: AutoLabelingInfoDto;
    onSubmit: (selectedModelLabel: string) => void;
    onDismiss: () => void;
};

const messages = defineMessages({
    title: { id: 'customText.selectTrainedModelScreen.title', defaultMessage: 'Select trained model' },
    f1ScoreTitle: { id: 'customText.selectTrainedModelScreen.f1ScoreTitle', defaultMessage: 'F1 score' },
    autotagTitle: { id: 'customText.selectTrainedModelScreen.autotagTitle', defaultMessage: 'Select trained model' },
    trainedModelLabel: { id: 'customText.selectTrainedModelScreen.trainedModelLabel', defaultMessage: 'Choose a trained model' },
    learnMoreLink: { id: 'customText.selectTrainedModelScreen.learnMoreLink', defaultMessage: 'Learn more about auto-labeling.' },
    emptyTrainedModels: {
        id: 'customText.selectTrainedModelScreen.emptyTrainedModels',
        defaultMessage: `You don't have trained models yet`
    },
    description: {
        id: 'customText.selectTrainedModelScreen.description',
        defaultMessage:
            'You can apply auto-labeling feature to allow the system to automatically label your data based on your trained model. Start by choosing your model.'
    },
    trainedModelLabelPlaceholder: {
        id: 'customText.selectTrainedModelScreen.trainedModelLabelPlaceholder',
        defaultMessage: 'Choose a trained model'
    },
    trainedModelTooltipText: {
        id: 'customText.selectTrainedModelScreen.trainedModelTooltipText',
        defaultMessage: 'Choose a trained model'
    },
    autotagDetails: {
        id: 'customText.selectTrainedModelScreen.autotagDetails',
        defaultMessage:
            'Auto-labeling is a way to speed up labeling all of your files. Once you have trained a model, you can use that model to label new files. You can select the files you want to label and the entities you want to label with. The model will predict what label(s) should be applied to those documents. You can review these labels in the Label data page to check for accuracy.'
    },
    trainedModelImageAltText: {
        id: 'customText.selectTrainedModelScreen.trainedModelImageAltText',
        defaultMessage: 'Trained model image.'
    }
});

const { styleComponent } = getStylistV2('AutotagTriggerModalSelectTrainedModel');
const LabelContainer = styleComponent<ILabelProps>(Label)('LabelContainer', { display: 'flex' });

export const SelectTrainedModelScreen = observer((props: AutotagTriggerModalSelectTrainedModelProps) => {
    const { autotagInfoDto, onSubmit, onDismiss } = props;
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();

    const trainedModels: IDropdownOption[] = rootStore.currentCustomTextProjectStore.trainedModels.items.map(m => ({
        key: m.id,
        text: m.name,
        data: {
            secondaryText: `(${localize(messages.f1ScoreTitle)}: ${rootStore.currentCustomTextProjectStore.modelsEvaluationMap.get(m.name)
                ?.f1Score ?? '-'})`
        }
    }));

    const [selectedModel, setSelectedModel] = React.useState(trainedModels.find(model => model.text === autotagInfoDto.trainedModelLabel));
    const imageSrc = getAssetsUrl(`assets/images/customText/autotagLogo.svg`);

    const labelOnRender = (label: string, tooltipText: string | React.ReactNode, isRequired: boolean = true) => (
        <Stack horizontal verticalAlign="center">
            <LabelContainer styles={{ root: { selectors: { '::after': { paddingRight: 0 } } } }} required={isRequired}>
                {label}
            </LabelContainer>
            <CalloutButton content={tooltipText} />
        </Stack>
    );

    return (
        <Stack>
            <Stack
                styles={{ root: { minHeight: '400px', height: '100%', position: 'relative' } }}
                tokens={{ childrenGap: 15, padding: `0 0 5px ${AutotagTriggerContentLeftMargin}` }}>
                <Stack tokens={{ childrenGap: 6 }}>
                    <LuisText variant="xLarge" fontWeight={FontWeights.semibold as Property.FontWeight}>
                        {localize(messages.title)}
                    </LuisText>
                    <LuisText>
                        {localize(messages.description)}
                        &nbsp;
                        <ExternalLink href="https://aka.ms/ctc-project-types">{localize(messages.learnMoreLink)}</ExternalLink>
                    </LuisText>
                </Stack>
                <Stack.Item styles={{ root: { flex: 1 } }}>
                    <LuisDropdown
                        aria-required
                        options={trainedModels}
                        selectedKey={selectedModel?.key}
                        styles={{ root: { width: '50%' } }}
                        onChange={(_, o) => setSelectedModel(o)}
                        ariaLabel={localize(messages.trainedModelLabel)}
                        emptyStatePlaceholder={localize(messages.emptyTrainedModels)}
                        placeholder={localize(messages.trainedModelLabelPlaceholder)}
                        isLoading={rootStore.customTextProjectStoreHouse.tracker.isRunning}
                        onRenderLabel={() =>
                            labelOnRender(localize(messages.trainedModelLabel), localize(messages.trainedModelTooltipText))
                        }
                    />
                </Stack.Item>
                <Stack.Item styles={{ root: { flex: 2, backgroundColor: '#E4E4E4' } }}>
                    <Stack tokens={{ childrenGap: 15, padding: 10 }}>
                        <LuisText fontWeight={FontWeights.semibold as Property.FontWeight}>{localize(messages.autotagTitle)}</LuisText>
                        <LuisText>{localize(messages.autotagDetails)}</LuisText>
                        <Stack.Item align="center">
                            <Image width="100%" height="100%" src={imageSrc} alt={localize(messages.trainedModelImageAltText)} />
                        </Stack.Item>
                    </Stack>
                </Stack.Item>
            </Stack>

            <AutoLabelingModalFooter
                onDismiss={onDismiss}
                isValid={Boolean(selectedModel)}
                onNext={() => onSubmit(selectedModel.text)}
                activeStep={AutoLabelingSteps.ConfigureJob}
                isLoading={rootStore.customTextProjectStoreHouse.tracker.isRunning}
            />
        </Stack>
    );
});
